import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { X, Plus, Save } from "lucide-react";

const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
const Chip = ({ on, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`px-2 py-1 border text-[10px] uppercase tracking-widest font-bold transition-colors ${on ? "border-[#E50000] bg-[#E50000]/15 text-white" : "border-[#333] text-[#737373] hover:text-white"}`}>
    {children}
  </button>
);

export function BookingForm({ rooms, initial, onClose, onDone }) {
  const [f, setF] = useState({ room_id: rooms[0]?.id || "", title: "", start: "10:00", end: "11:00", repeat_weeks: 0,
    approver_ids: [], participants: [], clubs: [], teams: [], ...initial });
  const [opts, setOpts] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/room-booking-options").then((r) => setOpts(r.data)).catch(() => setOpts({ approvers: [], people: [], teams: [], clubs: [] })); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (draft) => {
    setBusy(true);
    try {
      const body = { ...f, repeat_weeks: Number(f.repeat_weeks) || 0, draft, draft_id: f.id || f.draft_id || null };
      delete body.id;
      const r = await api.post("/room-bookings", body);
      toast.success(draft ? "Rascunho guardado" : r.data.status === "approved" ? "Sala marcada e convites enviados" : "Pedido enviado — aguarda aprovação");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  const names = Object.fromEntries((opts?.people || []).map((p) => [p.id, p.name]));
  const matches = q.trim().length >= 2 ? (opts?.people || []).filter((p) => p.name?.toLowerCase().includes(q.toLowerCase()) && !f.participants.includes(p.id)).slice(0, 6) : [];
  return (
    <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="rooms-form">
      <div className="kw-card p-6 w-full max-w-lg space-y-3 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center"><span className="font-display font-black uppercase flex-1">Pedir sala</span>
          <button onClick={onClose} data-testid="rooms-form-close"><X className="w-4 h-4" /></button></div>
        <select className="kw-input w-full" value={f.room_id} onChange={set("room_id")} data-testid="rooms-form-room">
          {rooms.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.capacity} lugares)</option>)}
        </select>
        <input className="kw-input w-full" placeholder="Motivo (ex.: formação, reunião com cliente)" value={f.title} onChange={set("title")} data-testid="rooms-form-title" />
        <div className="grid grid-cols-3 gap-2">
          <input type="date" className="kw-input" value={f.date} onChange={set("date")} data-testid="rooms-form-date" />
          <input type="time" className="kw-input" value={f.start} onChange={set("start")} data-testid="rooms-form-start" />
          <input type="time" className="kw-input" value={f.end} onChange={set("end")} data-testid="rooms-form-end" />
        </div>
        <label className="text-xs text-[#A3A3A3] flex items-center gap-2">Repetir nas
          <input type="number" min="0" max="26" className="kw-input !w-20 !py-1" value={f.repeat_weeks} onChange={set("repeat_weeks")} data-testid="rooms-form-repeat" />
          semanas seguintes</label>
        {opts && (
          <>
            <div><div className="kw-overline text-[10px] mb-1">Que administrador aprova (vazio = todos)</div>
              <div className="flex flex-wrap gap-1.5">{opts.approvers.map((a) => (
                <Chip key={a.id} on={f.approver_ids.includes(a.id)} onClick={() => setF({ ...f, approver_ids: toggle(f.approver_ids, a.id) })}
                  testid={`rooms-approver-${a.id}`}>{a.name}</Chip>))}</div></div>
            <div><div className="kw-overline text-[10px] mb-1">Convidar clubes inteiros</div>
              <div className="flex flex-wrap gap-1.5">{opts.clubs.map((c) => (
                <Chip key={c} on={f.clubs.includes(c)} onClick={() => setF({ ...f, clubs: toggle(f.clubs, c) })} testid={`rooms-club-${c}`}>{c}</Chip>))}</div></div>
            {opts.teams.length > 0 && (
              <div><div className="kw-overline text-[10px] mb-1">Convidar equipas</div>
                <div className="flex flex-wrap gap-1.5">{opts.teams.map((t) => (
                  <Chip key={t.id} on={f.teams.includes(t.id)} onClick={() => setF({ ...f, teams: toggle(f.teams, t.id) })} testid={`rooms-team-${t.id}`}>{t.name}</Chip>))}</div></div>
            )}
            <div><div className="kw-overline text-[10px] mb-1">Convidar pessoas</div>
              <input className="kw-input w-full !py-1.5 text-xs" placeholder="Escreve um nome…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="rooms-people-search" />
              {matches.map((p) => (
                <button key={p.id} onClick={() => { setF({ ...f, participants: [...f.participants, p.id] }); setQ(""); }}
                  className="block w-full text-left text-xs px-2 py-1 hover:bg-[#1a1a1a]">{p.name} <span className="text-[#555]">{p.club}</span></button>))}
              <div className="flex flex-wrap gap-1.5 mt-1.5">{f.participants.map((id) => (
                <Chip key={id} on onClick={() => setF({ ...f, participants: f.participants.filter((x) => x !== id) })}>{names[id] || "…"} ×</Chip>))}</div>
              <p className="text-[10px] text-[#555] mt-1">Os convidados recebem o convite com o calendário quando a sala for aprovada.</p>
            </div>
          </>
        )}
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => submit(true)} disabled={busy} className="kw-btn-secondary" data-testid="rooms-form-draft">Gravar rascunho</button>
          <button onClick={() => submit(false)} disabled={busy || !f.title.trim()} className="kw-btn-primary" data-testid="rooms-form-submit">Enviar pedido</button>
        </div>
      </div>
    </div>
  );
}

export function RoomsEditor({ rooms, onChange }) {
  const [edit, setEdit] = useState({});
  const save = async (r) => {
    const v = edit[r.id] || r;
    try {
      await api.patch(`/rooms/${r.id}`, { name: v.name, capacity: Number(v.capacity) || 1 });
      toast.success(`${v.name} guardada`);
      setEdit((e) => { const n = { ...e }; delete n[r.id]; return n; });
      onChange();
    }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const add = async () => {
    try { await api.post("/rooms", { name: `Sala ${rooms.length + 1}`, capacity: 10 }); onChange(); } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Remover ${r.name}?`)) return;
    try { await api.delete(`/rooms/${r.id}`); onChange(); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="kw-card p-5 space-y-2 max-w-2xl" data-testid="rooms-editor">
      <div className="kw-overline">Salas (só administradores)</div>
      <p className="text-[11px] text-[#737373] mb-2">Escreve o nome e o número de lugares e carrega em <b>Guardar</b> (ou Enter) na linha dessa sala.</p>
      {rooms.map((r) => {
        const v = edit[r.id] || r;
        const dirty = !!edit[r.id] && (v.name !== r.name || String(v.capacity) !== String(r.capacity));
        return (
          <div key={r.id} className="flex items-center gap-2">
            <input className="kw-input !py-1.5 flex-1" value={v.name} onChange={(e) => setEdit({ ...edit, [r.id]: { ...v, name: e.target.value } })}
              onKeyDown={(e) => e.key === "Enter" && save(r)} data-testid={`room-name-${r.id}`} />
            <input type="number" min="1" className="kw-input !py-1.5 !w-24" value={v.capacity} onChange={(e) => setEdit({ ...edit, [r.id]: { ...v, capacity: e.target.value } })}
              onKeyDown={(e) => e.key === "Enter" && save(r)} data-testid={`room-capacity-${r.id}`} />
            <span className="text-[10px] text-[#737373]">lugares</span>
            <button onClick={() => save(r)} disabled={!dirty} data-testid={`room-save-${r.id}`}
              className={`${dirty ? "kw-btn-primary" : "kw-btn-secondary opacity-40"} !py-1.5 !px-3 text-[10px] inline-flex items-center gap-1`}>
              <Save className="w-3 h-3" /> Guardar
            </button>
            <button onClick={() => remove(r)} title="Remover" className="text-[#FF6B6B]"><X className="w-4 h-4" /></button>
          </div>
        );
      })}
      <button onClick={add} className="kw-btn-secondary !py-1.5 text-[10px] inline-flex items-center gap-1" data-testid="room-add"><Plus className="w-3 h-3" /> Nova sala</button>
    </div>
  );
}
